import { useStyles } from '@webapps/styles';
import { InfoRounded } from '@material-ui/icons';
import { Button } from '@material-ui/core';
import { css, cx } from '@emotion/css';
import { useEditor } from 'context/editor/useEditor';
import { useAuth, useMutation, useQuery } from '@webapps/reactjs-tech-components';
import { disableNewVersion, getAllConventionModels } from '@webapps/business';
import { handleFindDocumentByIdInStore } from 'pages/CFA/Convention/utils/services';
import { useEffect, useState, useRef } from 'react';
import { getPaginationNumbers } from 'pages/CFA/Convention/utils/pagination';
import Pagination from '../Pagination';
import { ADD_DOCUMENT, UPDATE_DOCUMENT, DELETE_DOCUMENT } from 'context/editor/editorActions';
import { cfaDefaultModel } from 'config/constants';
import { InfoTooltip } from '@webapps/styles';
import { useHistory } from 'react-router-dom';

export default function ConventionModelContainer({ selectedDocument, setSelectedDocument, setCurrentPageIndex }) {

  const { push } = useHistory();
  const { classes, global } = useStyles(styles);
  const { state: stateEditor, dispatch: dispatchEditor } = useEditor();
  const { idUser } = useAuth();
  const { data: conventionModels = [], refetch, isLoading } = useQuery(getAllConventionModels, {
    key: 'conventionModels',
    variables: { id: idUser },
  });

  const {
    error: errorRequestDisableNewVersion,
    isLoading: isLoadingDisableNewVersion,
    isSuccess: isSuccessDisableNewVersion,
    mutate: mutateDisableNewVersion,
  } = useMutation(disableNewVersion);

  const [currentPage, setCurrentPage] = useState(1);
  const [mergedDocuments, setMergedDocuments] = useState([]);
  const [paginatedDocuments, setPaginatedDocuments] = useState([]);

  // Ref pour traquer les documents qui ont déjà été mis à jour pour éviter la boucle infinie
  const updatedDocumentsRef = useRef(new Set());

  const ITEMS_PER_PAGE = 6;

  useEffect(() => {
    // Ne pas exécuter tant que les données ne sont pas chargées
    if (isLoading) {
      return;
    }


    console.log("🚀 ~ mergedDocuments ~ conventionModels:", conventionModels)

    const mergedDocuments = [...stateEditor.documents, ...conventionModels].reduce((acc, doc) => {
      if (!acc.some((d) => d?.id === doc?.id)) {
        acc.push(doc);
      }

      return acc.sort((a, b) => a.name - b.name);
    }, []);

    for (const conventionModel of conventionModels) {
      // Si une nouvelle version est disponible
      if (conventionModel?.newVersionAvailable) {
        console.log("stateEditor.documents :", stateEditor.documents);
        console.log("conventionModel :", conventionModel);

        // Vérifier si ce document a déjà été mis à jour
        const updateKey = `${conventionModel.id}_${conventionModel.version || conventionModel.updatedAt}`;

        if (stateEditor.documents.some((d) => d?.id === conventionModel?.id) &&
          !updatedDocumentsRef.current.has(updateKey)) {
          console.log("NEW VERSION !!!");

          mutateDisableNewVersion({ id: conventionModel.id });

          // Marquer ce document comme mis à jour
          updatedDocumentsRef.current.add(updateKey);

          // Remplacer directement le document dans le store au lieu de supprimer puis ajouter
          dispatchEditor({
            type: UPDATE_DOCUMENT,
            payload: {
              documentId: conventionModel.id,
              updatedDocument: conventionModel
            }
          });
        }
      }
    }
    // Ajouter le modèle par défaut seulement si aucun document n'existe ET que le chargement est terminé
    if (stateEditor.documents.length + conventionModels.length === 0 && !isLoading) {
      // console.log("🚀 ~ useEffect ~ stateEditor.documents.length + conventionModels.length === 0:", stateEditor.documents.length + conventionModels.length === 0)
      // console.log("🚀 ~ useEffect ~ conventionModels.length:", conventionModels.length)
      // console.log("🚀 ~ useEffect ~ stateEditor.documents.length:", stateEditor.documents.length)
      dispatchEditor({
        type: ADD_DOCUMENT,
        payload: { ...cfaDefaultModel, id: `default` },
      });
    }

    setMergedDocuments(mergedDocuments);
  }, [stateEditor.documents, conventionModels, isLoading]);

  useEffect(() => {
    // Ce n'est pas très opti mais c'est intéressant pour refetch après que l'element ait été supprimé
    refetch()
  }, [stateEditor.documents]);

  // Nettoyer le ref quand de nouveaux conventionModels arrivent (pour permettre la détection de nouvelles versions)
  useEffect(() => {
    if (conventionModels.length > 0) {
      // Garder seulement les entrées qui correspondent aux modèles actuels
      const currentModelIds = conventionModels.map(model => model.id);
      const updatedEntries = Array.from(updatedDocumentsRef.current).filter(key => {
        const modelId = key.split('_')[0];
        return currentModelIds.includes(modelId);
      });
      updatedDocumentsRef.current = new Set(updatedEntries);
    }
  }, [conventionModels]);

  const documentNumber = mergedDocuments.length;

  const totalPages = Math.ceil(documentNumber / ITEMS_PER_PAGE);
  const paginationNumbers = getPaginationNumbers(currentPage, totalPages, 7);

  useEffect(() => {
    const paginatedDocuments = mergedDocuments.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );
    setPaginatedDocuments(paginatedDocuments);
  }, [currentPage, mergedDocuments]);


  useEffect(() => {

    if (Object.keys(selectedDocument)?.length === 0) {

      if (paginatedDocuments?.length > 0) {
        setSelectedDocument(paginatedDocuments[0]);
      }
    }
  }, [paginatedDocuments]);


  const handleSelectDocument = (document) => {

    let documentInStore = handleFindDocumentByIdInStore(document.id, stateEditor);

    // Si le document n'est pas dans le store ça veut dire qu'il provient de la bdd.
    // On va l'ajouter dans le store pour pouvoir le modifier
    if (!documentInStore) {

      dispatchEditor({
        type: ADD_DOCUMENT,
        payload: { ...document },
      });
    }

    setCurrentPageIndex(1);
    setSelectedDocument({ ...document });
  };

  return (
    <div className={classes["framerDiv"]}>
      <div className={classes["framerDiv2"]}>
        <div className={classes["framerDiv3"]}>
          <div className={global.title}>Modèles de conventions</div>
          <InfoTooltip
            position="bottom"
            forceVisible={false}
            title="Comment ça marche ?"
            description="Retrouvez ici un résumé de votre comptabilité sur l'année, incluant les factures transmises, à venir, et leur statut de règlement."
            link={{ text: "En savoir plus" }}
          />
        </div>
        <div className={global.subTitle}>Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s,</div>
      </div>
      <div className={cx(classes["modelFileContainer"], css({
        ".MuiButton-label": {
          gap: "6px",
        }
      }))}>
        {
          paginatedDocuments?.map((document, index) => {
            const isSelected = document?.id === selectedDocument?.id;
            const buttonStyle = {
              backgroundColor: isSelected ? "#ffffff" : "#f3f7ff",
              borderColor: isSelected ? "#ffffff" : "#c0d9ff",
              cursor: isSelected ? "default" : "pointer",
            }
            return (
              <Button key={`model-${index}`} className={cx(classes["framerButton"], css({
                backgroundColor: isSelected ? "#ffffff" : "#f3f7ff",
                borderColor: isSelected ? "#ffffff" : "#c0d9ff",
                cursor: isSelected ? "default" : "pointer",
                "&: hover": {
                  backgroundColor: "#ffffff",
                  borderColor: "#ffffff",

                  "& img": {
                    opacity: 1,
                  }
                }
              }))}
                onClick={() => handleSelectDocument(document)}
              >
                <img className={cx(classes["framerImg"], css({
                  opacity: isSelected ? 1 : 0.35,
                }))} src="https://framerusercontent.com/images/9CA6fQZxLQplO3nLkBtTrpS0pIU.png" srcSet="https://framerusercontent.com/images/9CA6fQZxLQplO3nLkBtTrpS0pIU.png 760w" sizes="19px" alt="" />
                <div className={classes["framerP3"]}>{document?.name}</div>
              </Button>
            )
          }
          )
        }
      </div>



      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        paginationNumbers={paginationNumbers}
      />
    </div>
  );
}

const styles = ({ spacing, breakpoints }) => ({
  "framerDiv": {
    alignItems: "flex-start",
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column",
    height: "min-content",
    justifyContent: "flex-start",
    padding: "20px",
    width: "100%",
    gap: "20px",

    [breakpoints.down(1255)]: {
      width: "100%",
    },
  },
  "framerDiv2": {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    height: "min-content",
    justifyContent: "flex-start",
    width: "100%",
    gap: "10px",
    overflow: "visible",
  },
  "framerDiv3": {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    height: "min-content",
    justifyContent: "flex-start",
    width: "100%",
    gap: "6px",
    overflow: "visible",
  },
  "framerIcon87": {
    height: "16px",
    width: "16px",
    fill: "#a8caff",
  },
  "modelFileContainer": {
    display: "grid",
    gridAutoRows: "minmax(0px, 1fr)",
    gridTemplateColumns: "repeat(2, minmax(50px, 1fr))",
    gridTemplateRows: "repeat(3, minmax(0px, 1fr))",
    height: "min-content",
    justifyContent: "center",
    width: "100%",
    gap: "6px",
    overflow: "visible",

    [breakpoints.down(1255)]: {
      gridTemplateColumns: "repeat(3, minmax(50px, 1fr))",
      gridTemplateRows: "repeat(2, minmax(0px, 1fr))",
    },
  },
  "framerButton": {
    transition: "none",
    border: "1px solid #c0d9ff",
    alignItems: "center",
    backgroundColor: "#f3f7ff",
    borderRadius: "5px",
    display: "flex",
    flexDirection: "row",
    height: "100%",
    justifyContent: "flex-start",
    paddingBottom: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
    paddingTop: "6px",
    gap: "6px",
    width: "fit-content",
    width: "100%",
    fontWeight: "400",
    "&:hover": {
      "& .framerP3": {
        color: "#0060f9",
      },
      borderColor: "#0060f9",
      height: "min-content",
    },
  },
  "framerImg": {
    aspectRatio: "0.95 / 1",
    width: "19px",
  },
  "framerP3": {
    fontFamily: "\"Roboto\", sans-serif",
    fontSize: "13.5px",
    lineHeight: "13.5px",
    color: "#152652",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});